import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/errors";
import EmailPanel from "@/components/EmailPanel";
import { Sparkles, Bot, User as UserIcon } from "lucide-react";

const CATEGORIES = ["slogan", "strategy", "business_plan", "presentation", "artwork"];

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("slogan");
  const [type, setType] = useState("text");
  const [hook, setHook] = useState("");
  const [explanation, setExplanation] = useState("");
  const [aiSide, setAiSide] = useState("A");
  const [contentA, setContentA] = useState("");
  const [contentB, setContentB] = useState("");
  const [noteA, setNoteA] = useState("");
  const [noteB, setNoteB] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");

  // Sponsor (optional)
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorUrl, setSponsorUrl] = useState("");
  const [sponsorLogo, setSponsorLogo] = useState("");
  const [sponsorTagline, setSponsorTagline] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="admin-no-access">
        <h1 className="font-display font-bold text-2xl mb-2">Admin only</h1>
        <p style={{ color: "var(--text-dim)" }}>You need admin access to create challenges.</p>
      </div>
    );
  }

  const onGenerate = async () => {
    if (!aiPrompt.trim()) { setError("Enter a prompt for the AI"); return; }
    setBusy(true); setError(""); setSuccess("");
    try {
      const endpoint = type === "image" ? "/ai/generate-image" : "/ai/generate-text";
      const payload = type === "image" ? { prompt: aiPrompt } : { prompt: aiPrompt, category };
      const { data } = await api.post(endpoint, payload);
      // place into the AI side
      if (aiSide === "A") {
        setContentA(data.content);
        setNoteA(type === "image" ? "Generated by Gemini Nano Banana" : "Generated by GPT-5.2");
      } else {
        setContentB(data.content);
        setNoteB(type === "image" ? "Generated by Gemini Nano Banana" : "Generated by GPT-5.2");
      }
      setSuccess("AI content generated and inserted.");
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setBusy(true);
    try {
      const payload = {
        title, category, type, hook, explanation,
        option_a: {
          label: "A", content: contentA,
          source: aiSide === "A" ? "ai" : "human",
          note: noteA || null,
        },
        option_b: {
          label: "B", content: contentB,
          source: aiSide === "B" ? "ai" : "human",
          note: noteB || null,
        },
      };
      const { data } = await api.post("/challenges", payload);
      setSuccess("Challenge published.");
      setTimeout(() => nav(`/challenges/${data.id}`), 700);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  };

  const previewImage = (s) => s && typeof s === "string" && s.startsWith("data:image");

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 py-16" data-testid="admin-page">
      <div className="label-mono mb-3" style={{ color: "var(--ai)" }}>Creator studio</div>
      <h1 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tighter mb-10">
        New Episode
      </h1>

      <EmailPanel />

      <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — Meta */}
        <div className="card p-6 space-y-4">
          <div className="label-mono">01 · Episode meta</div>
          <div>
            <label className="label-mono block mb-2">Title</label>
            <input className="input" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-2">Category</label>
              <select className="select" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="admin-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-2">Type</label>
              <select className="select" value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-type-select">
                <option value="text">Text</option>
                <option value="image">Image</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-mono block mb-2">AI option</label>
            <div className="flex gap-2">
              {["A", "B"].map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setAiSide(s)}
                  className={`btn ${aiSide === s ? "btn-ai" : "btn-secondary"}`}
                  data-testid={`admin-ai-side-${s}`}
                >
                  Option {s} is AI
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-mono block mb-2">Hook (optional)</label>
            <textarea className="textarea" value={hook} onChange={(e) => setHook(e.target.value)} placeholder="One of these slogans generated €1M…" data-testid="admin-hook-input" />
          </div>
          <div>
            <label className="label-mono block mb-2">Explanation (revealed after vote)</label>
            <textarea className="textarea" required value={explanation} onChange={(e) => setExplanation(e.target.value)} data-testid="admin-explanation-input" />
          </div>
          <div className="pt-3 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="label-mono mb-2" style={{ color: "#fcd34d" }}>Sponsor (optional)</div>
            <input className="input mb-2" placeholder="Sponsor name (e.g., Acme Inc.)" value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} data-testid="admin-sponsor-name-input" />
            <input className="input mb-2" placeholder="Sponsor URL" value={sponsorUrl} onChange={(e) => setSponsorUrl(e.target.value)} data-testid="admin-sponsor-url-input" />
            <input className="input mb-2" placeholder="Sponsor tagline (optional)" value={sponsorTagline} onChange={(e) => setSponsorTagline(e.target.value)} data-testid="admin-sponsor-tagline-input" />
            <input className="input" placeholder="Sponsor logo URL (optional)" value={sponsorLogo} onChange={(e) => setSponsorLogo(e.target.value)} data-testid="admin-sponsor-logo-input" />
          </div>
        </div>

        {/* MIDDLE — AI Generator */}
        <div className="card p-6 space-y-4" style={{ borderColor: "rgba(37,99,235,0.4)" }}>
          <div className="label-mono" style={{ color: "var(--ai)" }}>02 · AI generator <Sparkles size={12} className="inline" /></div>
          <div className="text-sm" style={{ color: "var(--text-dim)" }}>
            Generates {type === "image" ? "an image via Gemini Nano Banana" : "text via GPT-5.2"} and inserts it as Option {aiSide}.
          </div>
          <div>
            <label className="label-mono block mb-2">Prompt</label>
            <textarea className="textarea" rows={4} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder={type === "image" ? "A neon cyberpunk portrait of a CEO, ultra-detailed" : "Write a slogan for a new EV brand targeting Gen Z"} data-testid="admin-ai-prompt-input" />
          </div>
          <button type="button" className="btn btn-ai w-full" onClick={onGenerate} disabled={busy} data-testid="admin-generate-button">
            {busy ? "Generating…" : `Generate ${type === "image" ? "image" : "text"}`}
          </button>
          <div className="label-mono pt-2" style={{ color: "var(--text-dim)" }}>
            Tip: keep prompts specific.
          </div>
        </div>

        {/* RIGHT — Options */}
        <div className="space-y-6">
          {/* Option A */}
          <div className="card p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-mono">Option A</div>
              <span className={`tag flex items-center gap-1 ${aiSide === "A" ? "tag-ai" : "tag-human"}`}>
                {aiSide === "A" ? <><Bot size={11}/> AI</> : <><UserIcon size={11}/> Human</>}
              </span>
            </div>
            {type === "image" ? (
              <>
                <input className="input" placeholder="data:image/...;base64,… or URL" value={contentA} onChange={(e) => setContentA(e.target.value)} data-testid="admin-content-a-input" />
                {previewImage(contentA) && <img src={contentA} alt="" className="media-frame w-full h-40 object-cover" />}
              </>
            ) : (
              <textarea className="textarea" value={contentA} onChange={(e) => setContentA(e.target.value)} required data-testid="admin-content-a-input" />
            )}
            <input className="input" placeholder="Note (e.g., 'Apple, 1997')" value={noteA} onChange={(e) => setNoteA(e.target.value)} data-testid="admin-note-a-input" />
          </div>
          {/* Option B */}
          <div className="card p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-mono">Option B</div>
              <span className={`tag flex items-center gap-1 ${aiSide === "B" ? "tag-ai" : "tag-human"}`}>
                {aiSide === "B" ? <><Bot size={11}/> AI</> : <><UserIcon size={11}/> Human</>}
              </span>
            </div>
            {type === "image" ? (
              <>
                <input className="input" placeholder="data:image/...;base64,… or URL" value={contentB} onChange={(e) => setContentB(e.target.value)} data-testid="admin-content-b-input" />
                {previewImage(contentB) && <img src={contentB} alt="" className="media-frame w-full h-40 object-cover" />}
              </>
            ) : (
              <textarea className="textarea" value={contentB} onChange={(e) => setContentB(e.target.value)} required data-testid="admin-content-b-input" />
            )}
            <input className="input" placeholder="Note" value={noteB} onChange={(e) => setNoteB(e.target.value)} data-testid="admin-note-b-input" />
          </div>
        </div>

        {/* Footer actions full width */}
        <div className="lg:col-span-3 flex items-center justify-end gap-3 pt-4">
          {error && <div className="tag tag-wrong mr-auto" data-testid="admin-error">{error}</div>}
          {success && <div className="tag tag-correct mr-auto" data-testid="admin-success">{success}</div>}
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="admin-publish-button">
            {busy ? "Publishing…" : "Publish episode →"}
          </button>
        </div>
      </form>
    </div>
  );
}
